import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  ArrowLeft, Sparkle, DownloadSimple, UploadSimple, Plus, Trash, LinkSimple,
  ArrowsClockwise, PaperPlaneTilt, LockKey, CaretDown, CaretUp, Notebook, FileText, ShieldCheck,
} from "@phosphor-icons/react";
import { GroundingBadge, GroundingReport } from "../components/GroundingReport";
import { LensScopeDialog } from "../components/lens/LensScopeDialog";
import { Highlight } from "../components/lens/highlight";
import { FocusCard } from "../components/lens/FocusCard";

const STATUS_FLOW = ["draft", "in_review", "reconciling", "complete"];
const STATUS_LABEL = { draft: "Draft", in_review: "In Review", reconciling: "Reconciling", complete: "Complete" };
const FINDING_LABEL = { "": "—", pass: "Pass", gap: "Gap", followup: "Follow-up" };
const FINDING_STYLE = {
  pass: "bg-emerald-50 text-emerald-700 border-emerald-200",
  gap: "bg-red-50 text-red-700 border-red-200",
  followup: "bg-amber-50 text-amber-700 border-amber-200",
};
const SOURCE_BADGE = {
  ai: ["AI", "bg-indigo-50 text-indigo-700 border-indigo-200"],
  manual: ["Manual", "bg-muted text-muted-foreground border-border"],
  bank: ["Bank", "bg-teal-50 text-teal-700 border-teal-200"],
  transcript: ["Transcript", "bg-violet-50 text-violet-700 border-violet-200"],
  rsaw: ["RSAW", "bg-teal-50 text-teal-700 border-teal-200"],
  combined: ["RSAW+SME", "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"],
};
const READINESS_STYLE = {
  "Ready": "bg-emerald-50 border-emerald-200 text-emerald-800",
  "Mostly Ready": "bg-teal-50 border-teal-200 text-teal-800",
  "At Risk": "bg-amber-50 border-amber-200 text-amber-800",
  "Not Ready": "bg-red-50 border-red-200 text-red-800",
};
const CONF_STYLE = {
  high: "border-emerald-200 text-emerald-700",
  medium: "border-amber-200 text-amber-700",
  low: "border-red-200 text-red-700",
};

function ItemCard({ item, readOnly, evidenceList, onSave, onDelete, onApplyRsaw }) {
  const [edit, setEdit] = useState(item);
  const [showKnown, setShowKnown] = useState(false);
  const [showEvPicker, setShowEvPicker] = useState(false);
  const [evSel, setEvSel] = useState(item.evidence_ids || []);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setEdit(item); setEvSel(item.evidence_ids || []); }, [item]);
  const dirty = ["question", "expectation", "compliance_note", "sme_response", "evidence_cited", "finding"]
    .some((k) => (edit[k] || "") !== (item[k] || ""));
  const [srcLabel, srcStyle] = SOURCE_BADGE[item.source] || SOURCE_BADGE.manual;

  const save = async () => {
    setSaving(true);
    try { await onSave(item.id, edit); } finally { setSaving(false); }
  };
  const saveEvidence = async () => {
    setSaving(true);
    try { await onSave(item.id, { evidence_ids: evSel }); setShowEvPicker(false); } finally { setSaving(false); }
  };
  const linkedNames = (item.evidence_ids || [])
    .map((id) => evidenceList.find((e) => e.id === id)?.name || id).filter(Boolean);

  return (
    <div data-testid={`rr-item-${item.id}`} className="border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2 text-xs">
        <span className={`px-1.5 py-0.5 border ${srcStyle}`}>{srcLabel}</span>
        {item.part && <span className="px-1.5 py-0.5 border bg-muted text-muted-foreground border-border">Part {item.part}</span>}
        {item.finding && <span className={`px-1.5 py-0.5 border ${FINDING_STYLE[item.finding] || ""}`}>{FINDING_LABEL[item.finding]}</span>}
        {item.applied_to_rsaw && <span className="px-1.5 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1"><Notebook size={12} /> In RSAW</span>}
        <div className="flex-1" />
        {!readOnly && (
          <Button data-testid={`rr-item-delete-${item.id}`} variant="ghost" size="sm" className="h-6 text-muted-foreground hover:text-red-600" onClick={() => onDelete(item.id)}>
            <Trash size={14} />
          </Button>
        )}
      </div>

      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Walkthrough question</label>
        <Textarea data-testid={`rr-item-question-${item.id}`} value={edit.question || ""} disabled={readOnly} rows={2}
                  className="mt-1 text-sm" onChange={(e) => setEdit((s) => ({ ...s, question: e.target.value }))} />
      </div>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Auditor expectation (pass vs fail)</label>
        <Textarea data-testid={`rr-item-expectation-${item.id}`} value={edit.expectation || ""} disabled={readOnly} rows={2}
                  className="mt-1 text-sm" onChange={(e) => setEdit((s) => ({ ...s, expectation: e.target.value }))} />
      </div>

      {item.known_info && (
        <div className="text-xs">
          <button data-testid={`rr-item-known-toggle-${item.id}`} className="flex items-center gap-1 text-muted-foreground hover:text-foreground" onClick={() => setShowKnown((v) => !v)}>
            {showKnown ? <CaretUp size={12} /> : <CaretDown size={12} />} What we currently know
          </button>
          {showKnown && <pre className="mt-1 whitespace-pre-wrap font-sans bg-muted border border-border p-2 text-muted-foreground">{item.known_info}</pre>}
        </div>
      )}

      <div className="border border-amber-300 bg-amber-50 p-2">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-1">
          <LockKey size={12} /> Compliance-only note (never shown to the SME UI; hidden column in Excel)
        </label>
        <Textarea data-testid={`rr-item-note-${item.id}`} value={edit.compliance_note || ""} disabled={readOnly} rows={1}
                  className="mt-1 text-sm bg-card" onChange={(e) => setEdit((s) => ({ ...s, compliance_note: e.target.value }))} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">SME response</label>
          <Textarea data-testid={`rr-item-response-${item.id}`} value={edit.sme_response || ""} disabled={readOnly} rows={2}
                    className="mt-1 text-sm" onChange={(e) => setEdit((s) => ({ ...s, sme_response: e.target.value }))} />
        </div>
        <div className="space-y-2">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Evidence cited (free text)</label>
            <Input data-testid={`rr-item-evcited-${item.id}`} value={edit.evidence_cited || ""} disabled={readOnly}
                   className="mt-1 text-sm" onChange={(e) => setEdit((s) => ({ ...s, evidence_cited: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2">
            <Select value={edit.finding || "none"} disabled={readOnly}
                    onValueChange={(v) => setEdit((s) => ({ ...s, finding: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid={`rr-item-finding-${item.id}`} className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— finding —</SelectItem>
                <SelectItem value="pass">Pass</SelectItem>
                <SelectItem value="gap">Gap</SelectItem>
                <SelectItem value="followup">Follow-up</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid={`rr-item-link-evidence-${item.id}`} variant="outline" size="sm" className="rounded-none h-8 text-xs" disabled={readOnly}
                    onClick={() => setShowEvPicker(true)}>
              <LinkSimple size={13} className="mr-1" /> Link evidence {item.evidence_ids?.length ? `(${item.evidence_ids.length})` : ""}
            </Button>
          </div>
          {linkedNames.length > 0 && (
            <div className="text-[11px] text-muted-foreground truncate">Linked: {linkedNames.join(", ")}</div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        {dirty && !readOnly && (
          <Button data-testid={`rr-item-save-${item.id}`} size="sm" className="rounded-none h-8" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
        <div className="flex-1" />
        {!readOnly && (
          <Button data-testid={`rr-item-apply-rsaw-${item.id}`} variant="outline" size="sm" className="rounded-none h-8 text-xs"
                  disabled={item.applied_to_rsaw || !(item.sme_response || "").trim()}
                  title={item.applied_to_rsaw ? "Already applied" : !(item.sme_response || "").trim() ? "Needs an SME response first" : "Append this SME response to the requirement's RSAW narrative and link cited evidence"}
                  onClick={() => onApplyRsaw([item.id])}>
            <Notebook size={13} className="mr-1" /> {item.applied_to_rsaw ? "Applied to RSAW" : "Apply to RSAW"}
          </Button>
        )}
      </div>

      <Dialog open={showEvPicker} onOpenChange={setShowEvPicker}>
        <DialogContent data-testid={`rr-evidence-picker-${item.id}`} className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle>Link evidence artifacts (zero-footprint)</DialogTitle></DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-1">
            {evidenceList.length === 0 && <div className="text-sm text-muted-foreground">No evidence in the repository yet.</div>}
            {evidenceList.map((e) => (
              <label key={e.id} className="flex items-center gap-2 text-sm py-1 px-1 hover:bg-muted cursor-pointer">
                <Checkbox checked={evSel.includes(e.id)}
                          onCheckedChange={(v) => setEvSel((s) => v ? [...s, e.id] : s.filter((x) => x !== e.id))} />
                <span className="truncate">{e.name}</span>
                <span className="text-[10px] text-muted-foreground ml-auto">{e.source_type === "link" ? "link" : "file"}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setShowEvPicker(false)}>Cancel</Button>
            <Button data-testid={`rr-evidence-picker-save-${item.id}`} className="rounded-none" disabled={saving} onClick={saveEvidence}>Save links</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReqGroup({ req, items, readOnly, generating, evidenceList, onGenerate, onAdd, onAddBank, onSave, onDelete, onApplyRsaw }) {
  return (
    <div data-testid={`rr-req-group-${req.id}`} className="space-y-2">
      <div className="flex items-start gap-3 bg-muted border border-border px-3 py-2">
        <div className="flex-1 min-w-0">
          <span className="font-bold text-sm">{req.standard_id && req.cross ? `${req.standard_id} ` : ""}{req.req_number}</span>
          <span className="text-xs text-muted-foreground ml-2"><Highlight text={req.text} term={req.focusTerm} /></span>
          {!req.applicable && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-muted text-muted-foreground">Not applicable</span>}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1 shrink-0">
            <Button data-testid={`rr-gen-req-${req.id}`} variant="outline" size="sm" className="rounded-none h-7 text-xs" disabled={generating}
                    title="Generate (or regenerate) AI questions for this requirement — replaces its AI questions, keeps manual ones"
                    onClick={() => onGenerate(req.id)}>
              <Sparkle size={13} className="mr-1" /> {items.some((i) => i.source === "ai") ? "Regenerate AI" : "Generate AI"}
            </Button>
            <Button data-testid={`rr-add-req-${req.id}`} variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => onAdd(req)}>
              <Plus size={13} className="mr-1" /> Question
            </Button>
            <Button data-testid={`rr-bank-req-${req.id}`} variant="outline" size="sm" className="rounded-none h-7 text-xs" onClick={() => onAddBank(req)}>
              From bank
            </Button>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground pl-3">No questions yet for this requirement.</div>
      ) : items.map((it) => (
        <ItemCard key={it.id} item={it} readOnly={readOnly} evidenceList={evidenceList}
                  onSave={onSave} onDelete={onDelete} onApplyRsaw={onApplyRsaw} />
      ))}
    </div>
  );
}

export default function ClaroLensDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [review, setReview] = useState(null);  const [evidenceList, setEvidenceList] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [tenantLocked, setTenantLocked] = useState(false);
  const [premium, setPremium] = useState(false);
  const [quote, setQuote] = useState(null);
  const [smeEdit, setSmeEdit] = useState(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [addFor, setAddFor] = useState(null);
  const [addForm, setAddForm] = useState({ question: "", expectation: "", part: "", compliance_note: "" });
  const [bankFor, setBankFor] = useState(null);
  const [bankRows, setBankRows] = useState([]);
  const [bankSel, setBankSel] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [txPreview, setTxPreview] = useState(null);
  const [txBusy, setTxBusy] = useState(false);
  const [txSource, setTxSource] = useState("transcript"); // "transcript" | "rsaw" | "combined"
  const [txExclude, setTxExclude] = useState({});     // {item_id: true} excluded answers
  const [txNewExclude, setTxNewExclude] = useState({}); // {index: true} excluded new questions
  const [combineOpen, setCombineOpen] = useState(false);
  const [combineRsaw, setCombineRsaw] = useState(null);
  const [combineTranscript, setCombineTranscript] = useState(null);
  const fileRef = useRef(null);
  const txFileRef = useRef(null);
  const verifyFileRef = useRef(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const rsawFileRef = useRef(null);
  const pendingFile = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/rapid-reviews/${id}`);
      setReview(data);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error("This ClaroLens review isn't in the current registered entity — check the entity selector at the top of the sidebar.");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load review");
      }
      nav("/claro-lens");
    }
  }, [id, nav]);

  useEffect(() => {
    load();
    api.get("/evidence", { __silent402: true }).then(({ data }) => setEvidenceList(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/tenant/me", { __silent402: true }).then(({ data }) => setTenantLocked(!!data?.locked)).catch(() => {});
  }, [load]);

  // Read-only when the review is finalized OR the tenant is locked (expired trial /
  // suspended / access elapsed). A locked tenant must not be able to run AI or make
  // any changes — the backend also blocks these, this disables the controls so the
  // action is never offered in the first place.
  const readOnly = review?.status === "complete" || tenantLocked;

  const groups = useMemo(() => {
    if (!review) return [];
    const byReq = {};
    (review.items || []).forEach((i) => { (byReq[i.requirement_id] = byReq[i.requirement_id] || []).push(i); });
    return (review.requirements || [])
      .filter((r) => r.applicable || byReq[r.id]?.length)
      .map((r) => ({ req: { ...r, cross: review.cross_standard, focusTerm: review.focus?.term }, items: byReq[r.id] || [] }));
  }, [review]);

  const patchReview = async (body) => {
    try {
      const { data } = await api.patch(`/rapid-reviews/${id}`, body);
      setReview((r) => ({ ...r, ...data, requirements: r.requirements }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const generate = async (requirement_id = null) => {
    setGenerating(true);
    try {
      const res = await runAiJob(`/rapid-reviews/${id}/generate`, { requirement_id, premium });
      toast.success(`AI generated ${res.added} question(s) across ${res.requirements} requirement(s)`);
      if (res.skipped?.length) {
        toast.warning(`No AI output for ${res.skipped.join(", ")} — existing questions were kept. Regenerate to retry.`);
      }
      await load();
      fetchQuote();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Generation failed");
    } finally { setGenerating(false); }
  };

  const fetchQuote = useCallback(async () => {
    try {
      const { data } = await api.get(`/rapid-reviews/${id}/generate-quote?premium=${premium}`);
      setQuote(data);
    } catch { /* non-blocking */ }
  }, [id, premium]);
  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const saveItem = async (iid, body) => {
    try {
      await api.patch(`/rapid-reviews/${id}/items/${iid}`, body);
      toast.success("Saved");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };

  const deleteItem = async (iid) => {
    if (!window.confirm("Delete this question?")) return;
    try { await api.delete(`/rapid-reviews/${id}/items/${iid}`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  const applyRsaw = async (item_ids = null) => {
    try {
      const { data } = await api.post(`/rapid-reviews/${id}/apply-rsaw`, { item_ids });
      if (data.applied) toast.success(`${data.applied} SME response(s) applied to RSAW narratives`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} skipped: ${data.skipped.map((s) => s.reason).join("; ")}`);
      if (!data.applied && !data.skipped?.length) toast.info("Nothing to apply — no unapplied SME responses");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Apply failed"); }
  };

  const addItem = async () => {
    try {
      await api.post(`/rapid-reviews/${id}/items`, { requirement_id: addFor.id, ...addForm });
      setAddFor(null);
      setAddForm({ question: "", expectation: "", part: "", compliance_note: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Add failed"); }
  };

  const openBank = async (req) => {
    setBankFor(req); setBankSel([]);
    try {
      const { data } = await api.get(`/rfi-bank?standard_id=${encodeURIComponent(req.standard_id || review.standard_id)}`);
      const pref = (req.req_number || "").toUpperCase();
      setBankRows((data || []).filter((b) => (b.req_part || "").toUpperCase().startsWith(pref)));
    } catch { setBankRows([]); }
  };

  const addFromBank = async () => {
    try {
      for (const b of bankRows.filter((x) => bankSel.includes(x.id))) {
        await api.post(`/rapid-reviews/${id}/items`, {
          requirement_id: bankFor.id, question: b.question, part: "", source: "bank" });
      }
      toast.success(`${bankSel.length} question(s) added from the engagement bank`);
      setBankFor(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Add failed"); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get(`/rapid-reviews/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ClaroLens_${review.cross_standard ? "Cross-Standard" : review.standard_id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    pendingFile.current = f;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/rapid-reviews/${id}/import/preview`, fd);
      setImportPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not read that workbook");
    }
  };

  const applyImport = async () => {
    setImporting(true);
    const fd = new FormData();
    fd.append("file", pendingFile.current);
    try {
      const { data } = await api.post(`/rapid-reviews/${id}/import/apply`, fd);
      toast.success(`Applied ${data.applied} change(s)${data.added ? ` and added ${data.added} new question(s)` : ""} from the workbook`);
      setImportPreview(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  const onPickTranscript = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setTxBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const data = await runAiJob(`/rapid-reviews/${id}/transcript/preview`, fd);
      setTxSource("transcript");
      setTxExclude({});
      setTxNewExclude({});
      setTxPreview(data);
      if (!(data.changes || []).length && !(data.new_questions || []).length) {
        toast.info("The transcript didn't clearly match any workbook questions.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not analyze that transcript");
    } finally { setTxBusy(false); }
  };

  const onPickVerify = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setVerifyBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/rapid-reviews/${id}/verify-grounding`, fd);
      setReview((r) => ({ ...r, grounding_check: data }));
      toast.success(`Grounding checked — ${data.confidence}% confidence`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Grounding check failed");
    } finally { setVerifyBusy(false); }
  };

  const improveFileRef = useRef(null);
  const onPickImprove = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setVerifyBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/rapid-reviews/${id}/improve-grounding`, fd);
      setReview((r) => ({ ...r, items: data.items, grounding_check: null }));
      toast.success("Responses redrafted to match the transcript — re-verify to score them");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not improve responses");
    } finally { setVerifyBusy(false); }
  };

  const onPickRsaw = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setTxBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const data = await runAiJob(`/rapid-reviews/${id}/rsaw/preview`, fd);
      setTxSource("rsaw");
      setTxExclude({});
      setTxNewExclude({});
      setTxPreview(data);
      if (!(data.changes || []).length && !(data.new_questions || []).length) {
        toast.info("The RSAW didn't yield content for any workbook questions.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not analyze that RSAW");
    } finally { setTxBusy(false); }
  };

  const runCombined = async () => {
    if (!combineRsaw && !combineTranscript) {
      toast.error("Pick an RSAW and/or a transcript file first.");
      return;
    }
    setTxBusy(true);
    const fd = new FormData();
    if (combineRsaw) fd.append("rsaw_file", combineRsaw);
    if (combineTranscript) fd.append("transcript_file", combineTranscript);
    try {
      const data = await runAiJob(`/rapid-reviews/${id}/combined/preview`, fd);
      setCombineOpen(false);
      setCombineRsaw(null);
      setCombineTranscript(null);
      setTxSource("combined");
      setTxExclude({});
      setTxNewExclude({});
      setTxPreview(data);
      if (!(data.changes || []).length && !(data.new_questions || []).length) {
        toast.info("Neither file yielded content for the workbook questions.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Could not analyze those files");
    } finally { setTxBusy(false); }
  };

  const applyTranscript = async () => {
    setTxBusy(true);
    try {
      const answers = (txPreview.changes || [])
        .filter((c) => !txExclude[c.item_id])
        .map((c) => ({ item_id: c.item_id, ...c.changes }));
      const new_questions = (txPreview.new_questions || [])
        .filter((_, i) => !txNewExclude[i]);
      const { data } = await api.post(`/rapid-reviews/${id}/${txSource}/apply`, { answers, new_questions, readiness: txPreview.readiness });
      toast.success(`Applied ${data.applied} answer(s) and added ${data.added} follow-up question(s)`);
      setTxPreview(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Apply failed");
    } finally { setTxBusy(false); }
  };

  if (!review) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const idx = STATUS_FLOW.indexOf(review.status);

  return (
    <div data-testid="claro-lens-detail" className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Button data-testid="rr-back" variant="ghost" size="sm" onClick={() => nav("/claro-lens")}><ArrowLeft size={16} /></Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold truncate">{review.title}</h1>
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
            <span className="font-semibold" data-testid="rr-standards-label">{(review.standard_ids || [review.standard_id]).join(", ")}</span>
            {review.cross_standard && <span className="text-[10px] uppercase border border-primary/40 text-primary px-1">Cross-standard</span>}
            {review.focus?.term && (
              <span data-testid="rr-focus-badge" className="text-[10px] border border-amber-400 bg-amber-50 text-amber-800 px-1">
                Focus: {review.focus.term}{review.focus.functions?.length ? ` · ${review.focus.functions.join(", ")}` : ""}
              </span>
            )}
            {!readOnly && (
              <button data-testid="rr-edit-scope-btn" className="underline hover:text-foreground" onClick={() => setScopeOpen(true)}>
                Add / remove requirements
              </button>
            )}
            <span>·</span>
            {smeEdit === null ? (
              <button data-testid="rr-sme-edit-btn" className="hover:underline" title="Click to edit SME"
                      onClick={() => !readOnly && setSmeEdit({ sme_name: review.sme_name || "", sme_email: review.sme_email || "" })}>
                SME: {review.sme_name || "unassigned"}{review.sme_email ? ` (${review.sme_email})` : ""}
              </button>
            ) : (
              <span className="flex items-center gap-1">
                <Input data-testid="rr-sme-name-input" className="h-6 text-xs w-36" value={smeEdit.sme_name} placeholder="SME name"
                       onChange={(e) => setSmeEdit((s) => ({ ...s, sme_name: e.target.value }))} />
                <Input data-testid="rr-sme-email-input" className="h-6 text-xs w-44" value={smeEdit.sme_email} placeholder="SME email"
                       onChange={(e) => setSmeEdit((s) => ({ ...s, sme_email: e.target.value }))} />
                <Button data-testid="rr-sme-save" size="sm" className="h-6 rounded-none text-xs"
                        onClick={async () => { await patchReview(smeEdit); setSmeEdit(null); }}>Save</Button>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setSmeEdit(null)}>Cancel</Button>
              </span>
            )}
          </div>
        </div>
      </div>

      {tenantLocked && (
        <div data-testid="rr-readonly-banner" className="flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-900 px-4 py-2.5 text-sm">
          <span className="flex-1">
            Your Free Trial has ended — this ClaroLens review is <b>read-only</b>. AI generation, imports and edits are disabled. Subscribe to ClaroShield Light to resume.
          </span>
          <Button data-testid="rr-readonly-upgrade" size="sm" className="rounded-none bg-amber-600 hover:bg-amber-700 shrink-0"
                  onClick={() => nav("/billing")}>Upgrade</Button>
        </div>
      )}

      {/* status stepper + actions */}
      <div className="border border-border bg-card p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1" data-testid="rr-status-stepper">
          {STATUS_FLOW.map((s, i) => (
            <button key={s} data-testid={`rr-status-step-${s}`}
                    className={`px-2.5 py-1 text-xs border transition-colors ${
                      s === review.status ? "bg-slate-900 text-white border-slate-900 font-semibold"
                        : i < idx ? "bg-muted text-muted-foreground border-border"
                          : "bg-card text-muted-foreground border-border hover:border-slate-400"}`}
                    onClick={() => s !== review.status && patchReview({ status: s })}>
              {i + 1}. {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        {quote && quote.premium_allowed && (
          <div className="flex items-center gap-1.5" data-testid="rr-model-choice">
            <span className="text-xs text-muted-foreground">Analysis:</span>
            <Select value={premium ? "premium" : "standard"} onValueChange={(v) => setPremium(v === "premium")}>
              <SelectTrigger className="rounded-none h-8 w-44 text-xs" data-testid="rr-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (1× credits)</SelectItem>
                <SelectItem value="premium">Deep analysis (3× credits)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {quote && quote.per_req_pricing && (
          <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="rr-generate-cost"
                title={`${quote.requirements} applicable requirement(s) × ${premium ? 3 : 1} credit(s)`}>
            ~<b className={quote.credits > quote.remaining_credits ? "text-red-600" : "text-foreground"}>{quote.credits}</b> credit{quote.credits === 1 ? "" : "s"} · {quote.remaining_credits} left
          </span>
        )}
        <Button data-testid="rr-generate-all" variant="outline" size="sm" className="rounded-none" disabled={generating || readOnly}
                onClick={() => generate(null)}>
          {generating ? <ArrowsClockwise size={15} className="mr-1 animate-spin" /> : <Sparkle size={15} className="mr-1" />}
          {generating ? "Generating…" : "Generate all (AI)"}
        </Button>
        <Button data-testid="rr-export-btn" variant="outline" size="sm" className="rounded-none" onClick={exportXlsx}
                title="Round-trip Excel: SME Response / Evidence Cited / Finding are editable; compliance notes ride hidden.">
          <DownloadSimple size={15} className="mr-1" /> Export Excel
        </Button>
        <Button data-testid="rr-import-btn" variant="outline" size="sm" className="rounded-none" disabled={readOnly}
                onClick={() => fileRef.current?.click()}>
          <UploadSimple size={15} className="mr-1" /> Import edited
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="rr-import-file" onChange={onPickFile} />
        <Button data-testid="rr-transcript-btn" variant="outline" size="sm"
                className="rounded-none border-indigo-300 text-indigo-700 hover:bg-indigo-50" disabled={readOnly || txBusy}
                title="Upload a meeting transcript (Word or text) — AI fills SME responses, evidence & findings, flags follow-ups, and grades readiness"
                onClick={() => txFileRef.current?.click()}>
          {txBusy ? <ArrowsClockwise size={15} className="mr-1 animate-spin" /> : <Notebook size={15} className="mr-1" />}
          {txBusy ? "Analyzing…" : "Import transcript (AI)"}
        </Button>
        <input ref={txFileRef} type="file" accept=".docx,.txt" className="hidden" data-testid="rr-transcript-file" onChange={onPickTranscript} />
        <Button data-testid="rr-rsaw-btn" variant="outline" size="sm"
                className="rounded-none border-teal-300 text-teal-700 hover:bg-teal-50" disabled={readOnly || txBusy}
                title="Upload an existing RSAW (Word or text-based PDF) — AI mines the entity's documented narrative & evidence into 'known info', flags gaps as findings, and grades readiness"
                onClick={() => rsawFileRef.current?.click()}>
          {txBusy ? <ArrowsClockwise size={15} className="mr-1 animate-spin" /> : <FileText size={15} className="mr-1" />}
          {txBusy ? "Analyzing…" : "Import RSAW (AI)"}
        </Button>
        <input ref={rsawFileRef} type="file" accept=".docx,.pdf" className="hidden" data-testid="rr-rsaw-file" onChange={onPickRsaw} />
        <Button data-testid="rr-combined-btn" variant="outline" size="sm"
                className="rounded-none border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50" disabled={readOnly || txBusy}
                title="Import an RSAW and a meeting transcript together — the RSAW seeds background/known info while the transcript fills the live SME answers, in one pass"
                onClick={() => setCombineOpen(true)}>
          {txBusy ? <ArrowsClockwise size={15} className="mr-1 animate-spin" /> : <Sparkle size={15} className="mr-1" />}
          Import both (AI)
        </Button>
        <Button data-testid="rr-apply-all-rsaw" size="sm" className="rounded-none" disabled={readOnly}
                title="Push every unapplied SME response into its requirement's RSAW narrative and link cited evidence"
                onClick={() => applyRsaw(null)}>
          <PaperPlaneTilt size={15} className="mr-1" /> Apply all to RSAW
        </Button>
        <Button data-testid="rr-verify-grounding-btn" variant="outline" size="sm" className="rounded-none"
                disabled={verifyBusy}
                title="Upload the transcript/RSAW and reverse-check the captured SME responses against it (1 AI credit)"
                onClick={() => verifyFileRef.current?.click()}>
          {verifyBusy ? <ArrowsClockwise size={15} className="mr-1 animate-spin" /> : <ShieldCheck size={15} className="mr-1" />}
          {verifyBusy ? "Checking…" : "Verify grounding"}
        </Button>
        <input ref={verifyFileRef} type="file" accept=".docx,.txt,.pdf,.xlsx" className="hidden" data-testid="rr-verify-file" onChange={onPickVerify} />
        {review.grounding_check && (
          <Button data-testid="rr-improve-grounding-btn" variant="outline" size="sm"
                  className="rounded-none border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  disabled={verifyBusy}
                  title="Upload the transcript/RSAW and redraft the SME responses to match it (1 AI credit)"
                  onClick={() => improveFileRef.current?.click()}>
            <Sparkle size={15} weight="fill" className="mr-1" /> Improve with grounding
          </Button>
        )}
        <input ref={improveFileRef} type="file" accept=".docx,.txt,.pdf,.xlsx" className="hidden" data-testid="rr-improve-file" onChange={onPickImprove} />
      </div>

      {review.readiness && (
        <div data-testid="rr-readiness-panel"
             className={`border p-3 ${READINESS_STYLE[review.readiness.grade] || "bg-muted border-border text-foreground"}`}>
          <div className="flex items-center gap-4">
            <div className="text-center shrink-0">
              <div className="text-2xl font-bold leading-none" data-testid="rr-readiness-score">{review.readiness.score ?? "—"}</div>
              <div className="text-[10px] uppercase tracking-wide mt-0.5">Readiness</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm flex items-center gap-2">
                {review.readiness.grade}
                {review.readiness.source && <span className="text-[10px] font-normal opacity-70">from {review.readiness.source}</span>}
              </div>
              {review.readiness.summary && <div className="text-xs mt-0.5">{review.readiness.summary}</div>}
            </div>
            {(review.readiness_history || []).length > 1 && (
              <div className="hidden sm:flex items-end gap-1 shrink-0" data-testid="rr-readiness-history" title="Readiness across walkthroughs (oldest → latest)">
                {(review.readiness_history || []).slice(-8).map((h, i) => (
                  <div key={i} className="flex flex-col items-center gap-0.5">
                    <div className="w-3 bg-current opacity-40 rounded-sm"
                         style={{ height: `${Math.max(4, ((h.score ?? 0) / 100) * 36)}px` }} title={`${h.grade} · ${h.score ?? "—"} · ${(h.at || "").slice(0, 10)}`} />
                    <div className="text-[9px] opacity-60">{h.score ?? "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {review.grounding_check && (
        <div className="mb-3" data-testid="rr-grounding-wrap">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Grounding vs. uploaded source</span>
            <GroundingBadge check={review.grounding_check} testid="rr-grounding-badge" />
          </div>
          <GroundingReport check={review.grounding_check} history={review.grounding_history} testid="rr-grounding-report" />
        </div>
      )}

      <div className="mb-3">
        <FocusCard review={review} readOnly={readOnly} onSaved={(focus) => setReview((r) => ({ ...r, focus }))} />
      </div>

      {groups.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No applicable requirements found for {(review.standard_ids || [review.standard_id]).join(", ")} under the current entity profile.
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map(({ req, items }) => (
            <ReqGroup key={req.id} req={req} items={items} readOnly={readOnly} generating={generating}
                      evidenceList={evidenceList} onGenerate={(rid) => generate(rid)}
                      onAdd={(r) => setAddFor(r)} onAddBank={openBank}
                      onSave={saveItem} onDelete={deleteItem} onApplyRsaw={applyRsaw} />
          ))}
        </div>
      )}

      <LensScopeDialog open={scopeOpen} onOpenChange={setScopeOpen} review={review}
        onSaved={(data) => { setReview(data); fetchQuote(); }} />

      {/* add manual question */}
      <Dialog open={!!addFor} onOpenChange={(v) => !v && setAddFor(null)}>
        <DialogContent data-testid="rr-add-dialog" className="rounded-none">
          <DialogHeader><DialogTitle>Add question — {addFor?.cross ? `${addFor.standard_id} ` : ""}{addFor?.req_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea data-testid="rr-add-question" rows={3} placeholder="Walkthrough question…" value={addForm.question}
                      onChange={(e) => setAddForm((f) => ({ ...f, question: e.target.value }))} />
            <Textarea data-testid="rr-add-expectation" rows={2} placeholder="Auditor expectation (optional)…" value={addForm.expectation}
                      onChange={(e) => setAddForm((f) => ({ ...f, expectation: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="rr-add-part" placeholder="Subpart (e.g. 1.2, optional)" value={addForm.part}
                     onChange={(e) => setAddForm((f) => ({ ...f, part: e.target.value }))} />
            </div>
            <Textarea data-testid="rr-add-note" rows={1} placeholder="Compliance-only note (optional)…" value={addForm.compliance_note}
                      className="border-amber-300 bg-amber-50"
                      onChange={(e) => setAddForm((f) => ({ ...f, compliance_note: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setAddFor(null)}>Cancel</Button>
            <Button data-testid="rr-add-submit" className="rounded-none" disabled={!addForm.question.trim()} onClick={addItem}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* add from engagement question bank */}
      <Dialog open={!!bankFor} onOpenChange={(v) => !v && setBankFor(null)}>
        <DialogContent data-testid="rr-bank-dialog" className="rounded-none max-w-xl">
          <DialogHeader><DialogTitle>Common Engagement questions — {bankFor?.req_number}</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1">
            {bankRows.length === 0 && <div className="text-sm text-muted-foreground">No bank questions matched this requirement.</div>}
            {bankRows.map((b) => (
              <label key={b.id} className="flex items-start gap-2 text-sm py-1.5 px-1 hover:bg-muted cursor-pointer border-b border-border">
                <Checkbox className="mt-0.5" checked={bankSel.includes(b.id)}
                          onCheckedChange={(v) => setBankSel((s) => v ? [...s, b.id] : s.filter((x) => x !== b.id))} />
                <span><span className="text-[10px] text-muted-foreground mr-1">{b.req_part}</span>{b.question}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setBankFor(null)}>Cancel</Button>
            <Button data-testid="rr-bank-submit" className="rounded-none" disabled={!bankSel.length} onClick={addFromBank}>
              Add {bankSel.length || ""} selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* import preview */}
      <Dialog open={!!importPreview} onOpenChange={(v) => !v && setImportPreview(null)}>
        <DialogContent data-testid="rr-import-preview-dialog" className="rounded-none max-w-2xl">
          <DialogHeader><DialogTitle>Workbook changes ({importPreview?.count || 0}{(importPreview?.new_items || []).length ? ` · ${importPreview.new_items.length} new` : ""})</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {(importPreview?.changes || []).length === 0 && (importPreview?.new_items || []).length === 0 && (
              <div className="text-sm text-muted-foreground">No changes detected — the workbook matches the current review.</div>
            )}
            {(importPreview?.changes || []).map((c) => (
              <div key={c.item_id} className="border border-border p-2 text-xs">
                <div className="font-semibold">{c.req_label}{c.part ? ` · Part ${c.part}` : ""} — {c.question}</div>
                {Object.entries(c.changes || {}).map(([k, v]) => (
                  <div key={k} className="mt-1"><span className="text-muted-foreground">{k.replace("_", " ")}:</span> <span className="text-emerald-700">{String(v) || "(cleared)"}</span></div>
                ))}
                {(c.warnings || []).map((w, i) => <div key={i} className="text-amber-700 mt-1">⚠ {w}</div>)}
              </div>
            ))}
            {(importPreview?.new_items || []).length > 0 && (
              <>
                <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wide pt-1">New questions to add ({importPreview.new_items.length})</div>
                {(importPreview.new_items || []).map((n, i) => (
                  <div key={i} data-testid={`rr-import-newq-${i}`} className="border border-emerald-300 bg-emerald-50/40 p-2 text-xs">
                    <div className="font-semibold">{n.req_label}{n.part ? ` · Part ${n.part}` : ""} — {n.question}</div>
                    {n.expectation && <div className="mt-1 text-muted-foreground">expectation: {n.expectation}</div>}
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setImportPreview(null)}>Cancel</Button>
            <Button data-testid="rr-import-apply" className="rounded-none" disabled={importing || (!(importPreview?.changes || []).some((c) => Object.keys(c.changes || {}).length) && !(importPreview?.new_items || []).length)}
                    onClick={applyImport}>
              {importing ? "Applying…" : "Apply changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* combined RSAW + transcript picker */}
      <Dialog open={combineOpen} onOpenChange={(v) => { if (!txBusy) { setCombineOpen(v); if (!v) { setCombineRsaw(null); setCombineTranscript(null); } } }}>
        <DialogContent data-testid="rr-combined-dialog" className="rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkle size={18} /> Import RSAW + transcript together</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            The RSAW seeds each question's background (“known info”) and cited evidence; the transcript fills the live SME
            responses. Provide one or both — then review everything in a single preview before it's applied.
          </p>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">RSAW file <span className="font-normal text-muted-foreground">(.docx / .pdf)</span></div>
              <div className="flex items-center gap-2">
                <Button type="button" size="sm" variant="outline" className="rounded-none" data-testid="rr-combined-pick-rsaw"
                        onClick={() => document.getElementById("rr-combined-rsaw-input")?.click()}>
                  <FileText size={14} className="mr-1" /> Choose RSAW
                </Button>
                <span className="text-xs text-muted-foreground truncate" data-testid="rr-combined-rsaw-name">{combineRsaw?.name || "No file selected"}</span>
              </div>
              <input id="rr-combined-rsaw-input" type="file" accept=".docx,.pdf" className="hidden"
                     onChange={(e) => { setCombineRsaw(e.target.files?.[0] || null); e.target.value = ""; }} />
            </div>
            <div>
              <div className="text-xs font-semibold text-muted-foreground mb-1">Transcript file <span className="font-normal text-muted-foreground">(.docx / .txt)</span></div>
              <div className="flex items-center gap-2">
                <Button type="button" size="sm" variant="outline" className="rounded-none" data-testid="rr-combined-pick-transcript"
                        onClick={() => document.getElementById("rr-combined-tx-input")?.click()}>
                  <Notebook size={14} className="mr-1" /> Choose transcript
                </Button>
                <span className="text-xs text-muted-foreground truncate" data-testid="rr-combined-tx-name">{combineTranscript?.name || "No file selected"}</span>
              </div>
              <input id="rr-combined-tx-input" type="file" accept=".docx,.txt" className="hidden"
                     onChange={(e) => { setCombineTranscript(e.target.files?.[0] || null); e.target.value = ""; }} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" disabled={txBusy} onClick={() => setCombineOpen(false)}>Cancel</Button>
            <Button data-testid="rr-combined-run" className="rounded-none" disabled={txBusy || (!combineRsaw && !combineTranscript)}
                    onClick={runCombined}>
              {txBusy ? "Analyzing…" : "Analyze"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* transcript AI preview */}
      <Dialog open={!!txPreview} onOpenChange={(v) => !v && setTxPreview(null)}>
        <DialogContent data-testid="rr-transcript-preview-dialog" className="rounded-none max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {txSource === "rsaw" ? <FileText size={18} /> : txSource === "combined" ? <Sparkle size={18} /> : <Notebook size={18} />}
              {txSource === "rsaw" ? "RSAW analysis" : txSource === "combined" ? "Combined analysis (RSAW + transcript)" : "Transcript analysis"}
            </DialogTitle>
          </DialogHeader>

          {/* readiness banner */}
          {txPreview?.readiness && (
            <div data-testid="rr-transcript-readiness"
                 className={`border p-3 flex items-center gap-4 ${READINESS_STYLE[txPreview.readiness.grade] || "bg-muted border-border text-foreground"}`}>
              <div className="text-center shrink-0">
                <div className="text-2xl font-bold leading-none">{txPreview.readiness.score ?? "—"}{txPreview.readiness.score != null ? "" : ""}</div>
                <div className="text-[10px] uppercase tracking-wide mt-0.5">Readiness</div>
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm">{txPreview.readiness.grade}</div>
                <div className="text-xs mt-0.5">{txPreview.readiness.summary}</div>
              </div>
            </div>
          )}

          <div className="max-h-[50vh] overflow-y-auto space-y-3">
            {/* proposed answers */}
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Proposed answers ({(txPreview?.changes || []).filter((c) => !txExclude[c.item_id]).length}/{(txPreview?.changes || []).length})
            </div>
            {(txPreview?.changes || []).length === 0 && (
              <div className="text-sm text-muted-foreground">No existing questions were clearly answered in the transcript.</div>
            )}
            {(txPreview?.changes || []).map((c) => (
              <label key={c.item_id} data-testid={`rr-tx-change-${c.item_id}`}
                     className={`flex gap-2 border p-2 text-xs cursor-pointer ${txExclude[c.item_id] ? "opacity-50 border-border" : "border-border"}`}>
                <Checkbox data-testid={`rr-tx-change-check-${c.item_id}`} checked={!txExclude[c.item_id]} className="mt-0.5"
                          onCheckedChange={(v) => setTxExclude((s) => ({ ...s, [c.item_id]: !v }))} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold flex items-center gap-2">
                    <span>{c.req_label}{c.part ? ` · Part ${c.part}` : ""}</span>
                    {c.confidence && <span className={`text-[10px] px-1 border ${CONF_STYLE[c.confidence] || "border-border text-muted-foreground"}`}>{c.confidence} conf.</span>}
                  </div>
                  <div className="text-muted-foreground mt-0.5">{c.question}</div>
                  {Object.entries(c.changes || {}).filter(([k]) => k !== "evidence_ids").map(([k, v]) => (
                    <div key={k} className="mt-1">
                      <span className="text-muted-foreground">{k.replace("_", " ")}:</span>{" "}
                      {k === "finding"
                        ? <span className={`px-1 border ${FINDING_STYLE[v] || ""}`}>{FINDING_LABEL[v] || v}</span>
                        : <span className="text-emerald-700">{String(v)}</span>}
                    </div>
                  ))}
                  {(c.evidence_links || []).length > 0 && (
                    <div className="mt-1 flex items-start gap-1" data-testid={`rr-tx-evlink-${c.item_id}`}>
                      <LinkSimple size={12} className="mt-0.5 text-teal-600 shrink-0" />
                      <span className="text-teal-700">Auto-links evidence: {c.evidence_links.map((e) => e.name).join(", ")}</span>
                    </div>
                  )}
                  {c.rationale && <div className="text-muted-foreground italic mt-1">{c.rationale}</div>}
                </div>
              </label>
            ))}

            {/* new follow-up questions */}
            {(txPreview?.new_questions || []).length > 0 && (
              <>
                <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide pt-1">
                  New follow-up questions ({(txPreview?.new_questions || []).filter((_, i) => !txNewExclude[i]).length}/{(txPreview?.new_questions || []).length})
                </div>
                {(txPreview?.new_questions || []).map((nq, i) => (
                  <label key={i} data-testid={`rr-tx-newq-${i}`}
                         className={`flex gap-2 border p-2 text-xs cursor-pointer ${txNewExclude[i] ? "opacity-50 border-border" : "border-amber-300 bg-amber-50/40"}`}>
                    <Checkbox data-testid={`rr-tx-newq-check-${i}`} checked={!txNewExclude[i]} className="mt-0.5"
                              onCheckedChange={(v) => setTxNewExclude((s) => ({ ...s, [i]: !v }))} />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold flex items-center gap-2">
                        <span>{nq.req_label}{nq.part ? ` · Part ${nq.part}` : ""}</span>
                        {nq.finding && <span className={`px-1 border ${FINDING_STYLE[nq.finding] || ""}`}>{FINDING_LABEL[nq.finding] || nq.finding}</span>}
                      </div>
                      <div className="mt-0.5">{nq.question}</div>
                      {nq.known_info && <div className="mt-1"><span className="text-muted-foreground">known info:</span> <span className="text-muted-foreground">{nq.known_info}</span></div>}
                      {nq.sme_response && <div className="mt-1"><span className="text-muted-foreground">response:</span> <span className="text-emerald-700">{nq.sme_response}</span></div>}
                      {(nq.evidence_links || []).length > 0 && (
                        <div className="mt-1 flex items-start gap-1">
                          <LinkSimple size={12} className="mt-0.5 text-teal-600 shrink-0" />
                          <span className="text-teal-700">Auto-links evidence: {nq.evidence_links.map((e) => e.name).join(", ")}</span>
                        </div>
                      )}
                      {nq.rationale && <div className="text-muted-foreground italic mt-1">{nq.rationale}</div>}
                    </div>
                  </label>
                ))}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setTxPreview(null)}>Cancel</Button>
            <Button data-testid="rr-transcript-apply" className="rounded-none" disabled={txBusy}
                    onClick={applyTranscript}>
              {txBusy ? "Applying…" : "Apply to workbook"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
